/* DMR kapcsolatfigyelő - src/services/dmrConnectionWatchdog.js
 *
 * JAVÍTÁS (80. tétel - 32021425644): a DMR a II. félidő elején kb. 10 percig
 * nem kapott semmit a CR-től, és ezt sem az alkalmazás, sem a felhasználó nem
 * látta - csak egy kézi újratöltés hozta helyre. A DMR minden adatot élőben kap
 * (a 10 mp-es periodikus resync csak a helyi adatbázisból tölt újra), és a
 * socket.io egy "elhalt", de le nem zárt kapcsolatot csak a ping-időtúllépés
 * után (szerver: 15 mp pingInterval + 60 mp pingTimeout = 75 mp) vesz észre -
 * egy felfüggesztett/elaltatott lap vagy hálózat esetén ennél is később.
 *
 * Két jel alapján dönt:
 *  - SZERVER-PING: a szerver 15 mp-enként pingel. Ha PING_STALE_MS óta nem jött
 *    ping, a kapcsolat halott - a socket.io ezt magától csak később ismerné fel.
 *  - CR-ADAT: amíg a CR órája (a DMR szerint) jár, a CR másodpercenként küld
 *    óraszinkront. Ha CR_DATA_STALE_MS óta semmi nem jött, a DMR nem kap adatot.
 *
 * Ilyenkor jelzést ad (a felület figyelmeztetést mutat), és a MEGLÉVŐ, élesben
 * bevált újracsatlakozási utat indítja el (a transport lezárása -> a socket.io
 * automatikus újracsatlakozása -> a szokásos reconnect folyamat: újra-
 * bejelentkezés, MATCHDATA visszatöltés). Korlátok, hogy egy tartósan elérhetetlen
 * CR (pl. annak a hálózata ment el) ne okozzon újracsatlakozás-sorozatot:
 * legfeljebb MIN_FORCE_INTERVAL_MS-enként egyszer, és a CR-adat hiánya miatt
 * egymás után legfeljebb MAX_FORCED_WITHOUT_DATA alkalommal - utána csak jelez,
 * amíg adat nem érkezik.
 *
 * Csak a DMR felületen indul (DmrScreen.vue). Bugsnag: breadcrumb minden
 * beavatkozásnál, önálló esemény oldalbetöltésenként legfeljebb NOTIFY_MAX.
 */
import Bugsnag from "@bugsnag/js";

const CHECK_INTERVAL_MS = 5000;
const PING_STALE_MS = 45000;
const CR_DATA_STALE_MS = 30000;
const MIN_FORCE_INTERVAL_MS = 120000;
const MAX_FORCED_WITHOUT_DATA = 3;
const NOTIFY_MAX = 3;

let timer = null;
let socket = null;
let options = null;
let lastPingAt = 0;
let lastCrDataAt = 0;
let lastForcedAt = 0;
let forcedWithoutData = 0;
let notifies = 0;
let staleShown = false;
let listeners = [];
let owner = null;

function bugsnagReady() {
    return typeof Bugsnag.isStarted === 'function' && Bugsnag.isStarted();
}

function setStale(stale, info) {
    if (stale === staleShown && !stale) return;
    staleShown = stale;
    if (options && typeof options.onStatusChange === 'function') {
        try {
            options.onStatusChange(stale, info || {});
        } catch (e) {
            console.error(e);
        }
    }
}

function isClockRunning() {
    try {
        return !!(options && typeof options.isClockRunning === 'function' && options.isClockRunning());
    } catch (e) {
        return false;
    }
}

function forceReconnect(reason, sinceMs) {
    const now = Date.now();
    lastForcedAt = now;
    if (reason === 'no-cr-data') forcedWithoutData++;
    // Türelmi idő az újracsatlakozásra - ne értékeljük azonnal újra.
    lastPingAt = now;

    const meta = {
        reason: reason,
        sinceMs: Math.round(sinceMs),
        clockRunning: isClockRunning(),
        forcedWithoutData: forcedWithoutData,
        hidden: typeof document !== 'undefined' ? document.hidden : undefined,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined
    };
    console.warn('[DMR-WATCHDOG] Újracsatlakozás kényszerítése:', meta);
    if (bugsnagReady()) {
        Bugsnag.leaveBreadcrumb('DMR stale connection', meta, 'state');
        if (notifies < NOTIFY_MAX) {
            notifies++;
            Bugsnag.notify('DMR stale connection: ' + reason, function (event) {
                event.severity = 'info';
                event.context = 'DMR stale connection (' + reason + ')';
                event.addMetadata('dmrWatchdog', meta);
            });
        }
    }

    try {
        if (socket && socket.io && socket.io.engine) {
            // A transport lezárása: a socket.io ugyanúgy kezeli, mint egy valódi
            // kapcsolatvesztést, és a beállított újracsatlakozással magától
            // visszacsatlakozik (a szokásos reconnect folyamattal).
            socket.io.engine.close();
        } else if (socket) {
            socket.disconnect();
            socket.connect();
        }
    } catch (e) {
        console.error('[DMR-WATCHDOG] Újracsatlakozás hiba:', e);
    }
}

function check() {
    if (!socket || !socket.connected) {
        // Lecsatlakozott állapotban a socket.io maga csatlakozik újra.
        return;
    }
    const now = Date.now();
    const sincePing = now - lastPingAt;
    const sinceData = now - lastCrDataAt;
    const pingStale = sincePing > PING_STALE_MS;
    const dataStale = isClockRunning() && sinceData > CR_DATA_STALE_MS;

    if (!pingStale && !dataStale) {
        if (staleShown) setStale(false);
        return;
    }

    const reason = pingStale ? 'no-server-ping' : 'no-cr-data';
    const sinceMs = pingStale ? sincePing : sinceData;
    setStale(true, {reason: reason, sinceMs: sinceMs});

    if (now - lastForcedAt < MIN_FORCE_INTERVAL_MS) return;
    if (reason === 'no-cr-data' && forcedWithoutData >= MAX_FORCED_WITHOUT_DATA) return;
    forceReconnect(reason, sinceMs);
}

function on(target, event, fn) {
    target.on(event, fn);
    listeners.push(() => target.off(event, fn));
}

export default {
    /**
     * @param {Object} opts
     *   isClockRunning: () => boolean  - a CR órája a DMR szerint jár-e
     *   onStatusChange: (stale, info) => void - figyelmeztetés ki/be
     *   owner: a hívó (komponens) - a stop() csak a saját indítását állítja le
     */
    start(opts) {
        // Ha még fut (pl. a DmrScreen újraépül: az új példány created()-je a
        // régi beforeDestroy()-a ELŐTT fut le), az új indítás veszi át.
        if (timer) this.stop();
        socket = window.vueio;
        if (!socket) return;
        options = opts || {};
        owner = options.owner || null;
        const now = Date.now();
        lastPingAt = now;
        lastCrDataAt = now;
        lastForcedAt = 0;
        forcedWithoutData = 0;
        staleShown = false;

        if (socket.io) {
            on(socket.io, 'ping', () => { lastPingAt = Date.now(); });
        }
        // A CR felől (a szerveren át) érkező élő adat: 'sync' (óraszinkron,
        // periódusváltás stb.) és 'dmrpush' (események). onAny-vel figyeljük, mert
        // a clock.js initClock()-ja `window.vueio.off('sync')`-kal minden
        // 'sync'-figyelőt leválaszt - az onAny-t ez nem érinti.
        if (typeof socket.onAny === 'function') {
            const anyFn = (event) => {
                if (event === 'sync' || event === 'dmrpush') this.noteCrData();
            };
            socket.onAny(anyFn);
            listeners.push(() => socket.offAny(anyFn));
        }
        // (Újra)csatlakozás után a visszatöltés úgyis friss adatot hoz - türelmi idő.
        on(socket, 'connect', () => {
            const t = Date.now();
            lastPingAt = t;
            lastCrDataAt = t;
        });

        this._onVisibility = () => {
            if (!document.hidden) check();
        };
        document.addEventListener('visibilitychange', this._onVisibility);

        timer = setInterval(check, CHECK_INTERVAL_MS);
    },

    // Bármilyen, a CR-től (vagy a szerveren át) érkező adat jelzése.
    noteCrData() {
        if (!timer) return;
        lastCrDataAt = Date.now();
        forcedWithoutData = 0;
        if (staleShown && socket && socket.connected && Date.now() - lastPingAt <= PING_STALE_MS) {
            setStale(false);
        }
    },

    stop(stoppingOwner) {
        // Egy korábbi példány leállítása ne állítsa le az újat (l. start()).
        if (stoppingOwner !== undefined && stoppingOwner !== owner) return;
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
        listeners.forEach(off => { try { off(); } catch (e) { /* ignore */ } });
        listeners = [];
        if (this._onVisibility) {
            document.removeEventListener('visibilitychange', this._onVisibility);
            this._onVisibility = null;
        }
        if (staleShown) setStale(false);
        socket = null;
        options = null;
        owner = null;
    },

    // Teszteléshez / diagnosztikához
    _state() {
        return {lastPingAt, lastCrDataAt, lastForcedAt, forcedWithoutData, notifies, staleShown, running: !!timer};
    }
};
